import { useState, useEffect, useRef } from 'react';
import { Session, DayMap, OverridesMap, RawEntry } from '@/types';
import { syncFromDb, syncToDb, syncFromHrx, HrxSyncResult } from '@/lib/storage';
import { computeSessions, groupByDate } from '@/lib/parser';

export type TabType = 'import' | 'log' | 'weekly' | 'monthly' | 'pay';

export const useHooks = () => {
  const [activeTab, setActiveTab] = useState<TabType>('import');
  const [entries, setEntries] = useState<RawEntry[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [dayMap, setDayMap] = useState<DayMap>({});
  const [overrides, setOverrides] = useState<OverridesMap>({});
  const [darkMode, setDarkMode] = useState(true);
  const [isHydrated, setIsHydrated] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isNewPasscode, setIsNewPasscode] = useState(false);
  const [pendingPasscode, setPendingPasscode] = useState('');
  const [activePasscode, setActivePasscode] = useState('');

  const isWritingRef = useRef(false);

  // Rate states
  const [rateType, setRateType] = useState<'daily' | 'hourly'>('daily');
  const [dailyRate, setDailyRate] = useState('1000');
  const [hourlyRate, setHourlyRate] = useState('125');

  const fetchData = async (passcodeToUse = activePasscode) => {
    if (!passcodeToUse) return;
    try {
      const data = await syncFromDb(passcodeToUse);
      if (data) {
        setEntries(data.entries || []);
        setOverrides(data.overrides || {});
        setRateType(data.rateType || 'daily');
        setDailyRate(String(data.dailyRate ?? '1000'));
        setHourlyRate(String(data.hourlyRate ?? '125'));
      }
    } catch (err) {
      console.error('Failed to fetch from db:', err);
    }
  };

  // Derive sessions and dayMap whenever entries or overrides change
  useEffect(() => {
    const computed = computeSessions(entries);
    const grouped = groupByDate(computed);

    // Drop dates that ended up with 0h from the grouped map. These are
    // usually "orphan OUT only" days (a night-shift OUT landed on the next
    // calendar day) — they have entries but no session, so showing "0h 0m"
    // on the day is misleading. The hours are already attributed to the
    // IN's date in grouped from the session builder.
    Object.keys(grouped).forEach((date) => {
      if (grouped[date] === 0) delete grouped[date];
    });

    // Apply overrides
    Object.entries(overrides).forEach(([date, ov]) => {
      if (ov.hours === 0 && ov.status === '') {
        delete grouped[date];
      } else {
        grouped[date] = ov.hours;
      }
    });

    // Filter sessions to exclude deleted days
    const filteredSessions = computed.filter(session => {
      const ov = overrides[session.date];
      if (ov && ov.hours === 0 && ov.status === '') {
        return false;
      }
      return true;
    });

    setSessions(filteredSessions);
    setDayMap(grouped);
  }, [entries, overrides]);

  // Initial authentication check & theme load
  useEffect(() => {
    const timer = setTimeout(() => {
      const authed = localStorage.getItem('app_authenticated') === 'true';
      setIsAuthenticated(authed);
      
      const active = localStorage.getItem('app_active_passcode') || '';
      setActivePasscode(active);

      const savedDark = localStorage.getItem('theme_dark');
      if (savedDark !== null) {
        setDarkMode(savedDark === 'true');
      } else {
        setDarkMode(true);
      }
      
      if (active) {
        fetchData(active);
      }
      setIsHydrated(true);
    }, 0);

    return () => clearTimeout(timer);
  }, []);

  // Theme class application
  useEffect(() => {
    if (!isHydrated) return;
    const htmlEl = document.documentElement;
    if (darkMode) {
      htmlEl.classList.add('dark');
      localStorage.setItem('theme_dark', 'true');
    } else {
      htmlEl.classList.remove('dark');
      localStorage.setItem('theme_dark', 'false');
    }
  }, [darkMode, isHydrated]);

  // Online status, focus, and visibility triggers
  useEffect(() => {
    if (!isHydrated) return;
    if (activePasscode) {
      fetchData(activePasscode);
    }

    const handleSyncTrigger = () => {
      if (isWritingRef.current) {
        console.log('Skipping focus/online/visibility sync trigger: write in progress');
        return;
      }
      if (activePasscode && typeof navigator !== 'undefined' && navigator.onLine !== false) {
        fetchData(activePasscode);
      }
    };

    window.addEventListener('online', handleSyncTrigger);
    window.addEventListener('focus', handleSyncTrigger);
    document.addEventListener('visibilitychange', handleSyncTrigger);

    return () => {
      window.removeEventListener('online', handleSyncTrigger);
      window.removeEventListener('focus', handleSyncTrigger);
      document.removeEventListener('visibilitychange', handleSyncTrigger);
    };
  }, [isHydrated, activePasscode]);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const handleLogin = async (password: string): Promise<{ success: boolean; isNew: boolean }> => {
    if (!password) return { success: false, isNew: false };
    try {
      const res = await fetch('/api/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passcode: password })
      });
      if (res.ok) {
        const data = await res.json();
        if (data.exists) {
          localStorage.setItem('app_authenticated', 'true');
          localStorage.setItem('app_active_passcode', password);
          setActivePasscode(password);
          setIsAuthenticated(true);
          await fetchData(password);
          return { success: true, isNew: false };
        } else {
          setPendingPasscode(password);
          setIsNewPasscode(true);
          return { success: false, isNew: true };
        }
      }
    } catch (err) {
      console.error('Login error:', err);
    }
    return { success: false, isNew: false };
  };

  const handleSignUp = async (): Promise<void> => {
    if (!pendingPasscode) return;
    try {
      const res = await fetch('/api/auth', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passcode: pendingPasscode })
      });
      if (res.ok) {
        localStorage.setItem('app_authenticated', 'true');
        localStorage.setItem('app_active_passcode', pendingPasscode);
        setActivePasscode(pendingPasscode);
        setIsAuthenticated(true);
        setIsNewPasscode(false);
        setPendingPasscode('');
        await fetchData(pendingPasscode);
      }
    } catch (err) {
      console.error('Sign up error:', err);
    }
  };

  const cancelSignUp = (): void => {
    setIsNewPasscode(false);
    setPendingPasscode('');
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('app_authenticated');
    localStorage.removeItem('app_active_passcode');
    setActivePasscode('');
    setEntries([]);
    setOverrides({});
  };

  const handleSaveOverride = async (date: string, hours: number, status: string) => {
    isWritingRef.current = true;
    try {
      const updated = { ...overrides, [date]: { hours, status } };
      setOverrides(updated);
      await syncToDb(activePasscode, { overrides: updated });
      await fetchData();
    } finally {
      isWritingRef.current = false;
    }
  };

  const handleDeleteOverride = async (date: string) => {
    isWritingRef.current = true;
    try {
      // Clear the override AND drop every entry that participates in a
      // session whose start date equals `date`. Without this, the next
      // paste for the same date collides with the surviving rows on
      // @@unique([passcode, date, type, time]).
      //
      // Overnight shifts are the gotcha: a session that starts Aug 3 21:48
      // and ends Aug 4 02:15 is attributed to Aug 3 (start date) by the
      // parser, but its OUT entry is stored with date='2026-08-04' (the
      // PHT calendar date of the OUT). Filtering by raw `e.date !== X`
      // would drop the OUT of an Aug 3 overnight session when the user
      // clears Aug 4 — verified 2026-08-04, leaving the Aug 3 IN orphaned.
      //
      // The correct filter: for each entry, determine its session's START
      // date (the IN's date). If that start date == `date`, drop both the
      // IN and the OUT, regardless of the OUT's raw calendar date.
      //
      // Approach: sort entries by PHT timestamp, then for each OUT find
      // the closest preceding unused IN within a 16h window (same logic
      // as computeSessions). Build a map of OUT-entry-index -> IN-entry-index
      // (their session pair). Then drop the pair if the IN's date == `date`.
      const phtMs = (e: RawEntry): number => {
        const [y, m, d] = e.date.split('-').map(Number);
        const phtMidnightUtc = Date.UTC(y, m - 1, d, 0, 0, 0) - 8 * 60 * 60 * 1000;
        return phtMidnightUtc + e.time * 60 * 60 * 1000;
      };
      const MAX_SESSION_HOURS = 16;

      // Sort entries by their PHT timestamp to mirror computeSessions.
      const indexed = entries.map((e, idx) => ({ e, idx, ts: phtMs(e) }));
      const byTs = [...indexed].sort((a, b) => a.ts - b.ts);
      const used = new Set<number>();
      const outToInIdx = new Map<number, number>(); // outEntryIdx -> inEntryIdx
      const sessionsByInIdx = new Map<number, { outIdx: number; inDate: string }>();

      for (let i = 0; i < byTs.length; i++) {
        if (used.has(byTs[i].idx)) continue;
        if (byTs[i].e.type !== 'IN') continue;
        // Find the closest OUT after this IN within 16h.
        let bestJ = -1;
        let bestDur = Infinity;
        for (let j = i + 1; j < byTs.length; j++) {
          if (used.has(byTs[j].idx)) continue;
          if (byTs[j].e.type !== 'OUT') continue;
          const dur = (byTs[j].ts - byTs[i].ts) / (60 * 60 * 1000);
          if (dur <= 0) continue;
          if (dur > MAX_SESSION_HOURS) continue;
          if (dur < bestDur) {
            bestDur = dur;
            bestJ = j;
          }
        }
        if (bestJ === -1) continue;
        const inIdx = byTs[i].idx;
        const outIdx = byTs[bestJ].idx;
        used.add(inIdx);
        used.add(outIdx);
        outToInIdx.set(outIdx, inIdx);
        sessionsByInIdx.set(inIdx, { outIdx, inDate: byTs[i].e.date });
      }

      // Now decide which entry indices to keep. An entry is dropped if
      // its session's IN date == `date`. That includes:
      //   - the IN itself (its date IS the IN's date)
      //   - the OUT (whose session's IN date is the prior day)
      //   - any orphan IN/OUT that didn't pair (falls back to entry.date)
      const dropIdx = new Set<number>();
      for (const { e, idx } of indexed) {
        let sessionStartDate: string;
        if (e.type === 'IN') {
          sessionStartDate = e.date;
        } else {
          // OUT — find paired IN
          const inIdx = outToInIdx.get(idx);
          if (inIdx !== undefined) {
            const sess = sessionsByInIdx.get(inIdx);
            sessionStartDate = sess?.inDate ?? e.date;
          } else {
            // Orphan OUT — fall back to entry's own date
            sessionStartDate = e.date;
          }
        }
        if (sessionStartDate === date) dropIdx.add(idx);
      }

      const remainingEntries = entries.filter((_, idx) => !dropIdx.has(idx));

      // Clear Record no longer creates an override. Earlier code wrote
      // `{hours:0, status:''}` to mark the day as "manually cleared", but
      // that override silently zeroes out the dayMap display even after a
      // subsequent HRX re-sync inserts fresh entries — the override stays
      // and the user sees 0h despite the data being there. Verified
      // 2026-08-05 (the Aug 4 sync bug).
      //
      // The fix: just delete the entries. The dayMap derives 0h from the
      // absence of entries on its own; no override row needed. Manual
      // hours overrides (e.g. "Aug 5 = holiday, 8h") live in handleSaveOverride
      // and are unaffected.
      //
      // If a stale override still exists on `date` from before this fix
      // landed, wipe it here too so old data doesn't haunt new sessions.
      const remainingOverrides = { ...overrides };
      if (remainingOverrides[date] !== undefined) {
        delete remainingOverrides[date];
      }
      setOverrides(remainingOverrides);
      setEntries(remainingEntries);
      await syncToDb(activePasscode, {
        entries: remainingEntries,
        overrides: Object.keys(remainingOverrides).length > 0 ? remainingOverrides : {},
      });
      await fetchData();
    } finally {
      isWritingRef.current = false;
    }
  };

  const handleUpdateRates = async (type: 'daily' | 'hourly', daily: string, hourly: string) => {
    isWritingRef.current = true;
    try {
      setRateType(type);
      setDailyRate(daily);
      setHourlyRate(hourly);
      await syncToDb(activePasscode, { rateType: type, dailyRate: daily, hourlyRate: hourly });
      await fetchData();
    } finally {
      isWritingRef.current = false;
    }
  };

  const handleImportEntries = async (mergedEntries: RawEntry[], importedDates: string[]) => {
    isWritingRef.current = true;
    try {
      const updatedOverrides = { ...overrides };
      let overridesChanged = false;
      importedDates.forEach(date => {
        if (updatedOverrides[date] !== undefined) {
          delete updatedOverrides[date];
          overridesChanged = true;
        }
      });

      setEntries(mergedEntries);
      if (overridesChanged) {
        setOverrides(updatedOverrides);
        await syncToDb(activePasscode, { entries: mergedEntries, overrides: updatedOverrides });
      } else {
        await syncToDb(activePasscode, { entries: mergedEntries });
      }
      await fetchData();
    } finally {
      isWritingRef.current = false;
    }
  };

  const handleClearAllEntries = async () => {
    isWritingRef.current = true;
    try {
      setEntries([]);
      setOverrides({});
      await syncToDb(activePasscode, { entries: [], overrides: {} });
      await fetchData();
    } finally {
      isWritingRef.current = false;
    }
  };

  // Pulls DTR sessions from HRX for the given date range and commits them
  // to the timelog DB. Breaks are filtered out by default (the timelog is
  // for paid work hours only). After a successful commit we re-fetch so the
  // UI shows the merged entries immediately.
  //
  // hrxCookie + personnelId are optional in the call — empty values let
  // the server fall back to the cached HrxSession for this passcode
  // (added 2026-08-04).
  const handleSyncFromHrx = async (body: {
    hrxCookie?: string;
    startsAt: string;
    endsAt: string;
    personnelId?: string;
    includeBreaks?: boolean;
    dryRun?: boolean;
  }): Promise<HrxSyncResult | null> => {
    isWritingRef.current = true;
    try {
      const result = await syncFromHrx(activePasscode, body);
      if (result && result.success && !result.dryRun) {
        await fetchData();
      }
      return result;
    } finally {
      isWritingRef.current = false;
    }
  };

  return {
    activeTab,
    setActiveTab,
    dayMap,
    sessions,
    overrides,
    darkMode,
    toggleDarkMode,
    isHydrated,
    isAuthenticated,
    isNewPasscode,
    pendingPasscode,
    activePasscode,
    handleLogin,
    handleSignUp,
    cancelSignUp,
    handleLogout,
    handleSaveOverride,
    handleDeleteOverride,
    
    // Rates
    rateType,
    dailyRate,
    hourlyRate,
    handleUpdateRates,

    // Entries & storage-less actions
    entries,
    handleImportEntries,
    handleClearAllEntries,
    handleSyncFromHrx
  };
};
